/**
 * Centralized API Client for GoTurkeyTransfer
 * 
 * This module provides a single source of truth for all API calls,
 * using relative paths to avoid ERR_CONNECTION_RESET issues.
 * 
 * Usage:
 *   import { apiGet, apiPost } from '../lib/api';
 *   const data = await apiGet('/api/airports');
 *   const result = await apiPost('/api/booking/start', { ... });
 */

const API_BASE = window.location.origin || '';

// Debug: Log API base URL (enable with ?debugEvents=1 or ?debugApi=1)
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debugEvents') === '1' || urlParams.get('debugApi') === '1') {
    console.log('[API] Base URL:', API_BASE || '(relative)');
    console.log('[API] ENV REACT_APP_BACKEND_URL:', window.location.origin);
  }
}

// Default timeout for API requests (10 seconds)
const DEFAULT_TIMEOUT = 10000;

/**
 * Creates an AbortController with timeout
 */
function createTimeoutController(timeout = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Makes a GET request to the API with timeout and retry support
 * @param {string} endpoint - API endpoint (e.g., '/api/airports')
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiGet(endpoint, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, retry = false, ...fetchOptions } = options;
  
  const attemptFetch = async () => {
    const { controller, timeoutId } = createTimeoutController(timeout);
    
    try {
      const url = `${API_BASE}${endpoint}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        signal: controller.signal,
        ...fetchOptions,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error(`[API] GET ${endpoint} failed:`, response.status, response.statusText);
        return null;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        console.error(`[API] GET ${endpoint} timeout after ${timeout}ms`);
      } else {
        console.error(`[API] GET ${endpoint} error:`, error.message);
      }
      return null;
    }
  };

  // First attempt
  let result = await attemptFetch();
  
  // Retry once if enabled and first attempt failed
  if (result === null && retry) {
    console.log(`[API] Retrying GET ${endpoint}...`);
    result = await attemptFetch();
  }
  
  return result;
}

/**
 * Makes a POST request to the API with timeout support
 * @param {string} endpoint - API endpoint (e.g., '/api/booking/start')
 * @param {Object} body - Request body
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiPost(endpoint, body = {}, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const { controller, timeoutId } = createTimeoutController(timeout);
  
  try {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
      ...fetchOptions,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[API] POST ${endpoint} failed:`, response.status, response.statusText);
      // Try to get error message from response
      try {
        const errorData = await response.json();
        return { success: false, error: errorData.error || errorData.detail || 'Request failed' };
      } catch {
        return { success: false, error: 'Request failed' };
      }
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.error(`[API] POST ${endpoint} timeout after ${timeout}ms`);
      return { success: false, error: 'Request timeout - please try again' };
    }
    
    console.error(`[API] POST ${endpoint} error:`, error.message);
    return { success: false, error: error.message || 'Connection error' };
  }
}

/**
 * Makes a PUT request to the API with timeout support
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiPut(endpoint, body = {}, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const { controller, timeoutId } = createTimeoutController(timeout);
  
  try {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
      ...fetchOptions,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[API] PUT ${endpoint} failed:`, response.status, response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.error(`[API] PUT ${endpoint} timeout after ${timeout}ms`);
    } else {
      console.error(`[API] PUT ${endpoint} error:`, error.message);
    }
    return null;
  }
}

/**
 * Makes a DELETE request to the API with timeout support
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiDelete(endpoint, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;
  const { controller, timeoutId } = createTimeoutController(timeout);
  
  try {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      signal: controller.signal,
      ...fetchOptions,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[API] DELETE ${endpoint} failed:`, response.status, response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.error(`[API] DELETE ${endpoint} timeout after ${timeout}ms`);
    } else {
      console.error(`[API] DELETE ${endpoint} error:`, error.message);
    }
    return null;
  }
}

/**
 * Makes an authenticated GET request (with JWT token)
 * @param {string} endpoint - API endpoint
 * @param {string} token - JWT token
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiAuthGet(endpoint, token, options = {}) {
  return apiGet(endpoint, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

/**
 * Makes an authenticated POST request (with JWT token)
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {string} token - JWT token
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiAuthPost(endpoint, body, token, options = {}) {
  return apiPost(endpoint, body, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

/**
 * Makes an authenticated PUT request (with JWT token)
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {string} token - JWT token
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiAuthPut(endpoint, body, token, options = {}) {
  return apiPut(endpoint, body, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

/**
 * Makes an authenticated DELETE request (with JWT token)
 * @param {string} endpoint - API endpoint
 * @param {string} token - JWT token
 * @param {Object} options - Optional fetch options
 * @returns {Promise<any>} - Response data or null on error
 */
export async function apiAuthDelete(endpoint, token, options = {}) {
  return apiDelete(endpoint, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

// Export API base for special cases
export const getApiBase = () => API_BASE;

// Default export for convenience
export default {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  authGet: apiAuthGet,
  authPost: apiAuthPost,
  authPut: apiAuthPut,
  authDelete: apiAuthDelete,
  getBase: getApiBase,
};
